import { ApiError, getJsonAt } from './client';
import type { ConsoleEnvironment } from '../config/consoleConfig';

// FD-422 / FD-ADR-040 §4 — `GET /v1/whoami`, and the one refusal the console
// treats as a statement about ACCESS rather than about health.
//
// The endpoint is deliberately the cheapest thing in the Manager: the tenant
// guard's own verdict is the entire answer, with no Graph call, no storage
// fan-out and no Key Vault read. That is what makes it usable as a
// per-environment boot probe rather than only as a diagnostic.

/** The projection `manager/whoami/run.ps1` returns under the ADR-007 `data` field. */
export interface WhoAmI {
  /** The caller's tenant. `null` only on a host with no EasyAuth in front of it. */
  tenantId: string | null;
  /** Display name, then UPN, then null. Never a fabricated placeholder. */
  name: string | null;
  /** Always an array — the endpoint guarantees empty rather than null. */
  roles: string[];
  /**
   * This deployment's own `ENVIRONMENT_NAME` app setting, read from the setting
   * and NEVER derived from a hostname.
   *
   * That distinction is the whole reason the drift check can work: a
   * hostname-derived value would agree with whichever host answered, which is
   * precisely the thing in question.
   */
  environmentName: string | null;
  /**
   * The deployment's DISPLAY label (FD-586) — "American Pain Consortium", not
   * `apc`. Optional and nullable: a Manager predating FD-586 omits it.
   *
   * It exists because `config.json` is public (fetched before sign-in) and a
   * multi-customer fleet therefore omits `label` there. This endpoint is the one
   * place a customer's name can be read, and only by a tenant the Manager admits
   * — so the label reaches exactly the people allowed to see it.
   *
   * It is a LABEL, not an identity claim: it never feeds the drift check (see
   * `detectEnvironmentDrift`), because a value the server reports cannot be used
   * to confirm what the same server reports.
   */
  environmentLabel?: string | null;
  /**
   * `single-tenant` | `multi-tenant`. Not a security control on either side: the
   * tenant boundary is `ALLOWED_TENANT_IDS`.
   *
   * FD-594 — the console now READS it for one presentational decision: on a
   * multi-tenant (Rubix-hosted) environment the new-campaign wizard does not
   * offer "this deployment's own Entra tenant" as a Hybrid destination, because
   * that tenant is Rubix's. The server enforces the same rule
   * (`400 DESTINATION_TENANT_REQUIRED`), so the console merely stops offering
   * what would be refused.
   */
  adminAuthMode: string | null;
}

/**
 * The origin to probe for one environment.
 *
 * Mirrors `resolveManagerBaseUrl`'s DEV rule rather than inventing a second one:
 * in DEV every request is relative so the Vite proxy makes the hop and the
 * Manager's CORS allowlist (which names only the SWA origin) is never tripped.
 * Probing `env.managerBaseUrl` directly in DEV would fail CORS on every
 * environment and hide nothing — a probe that is uniformly wrong is worse than
 * no probe, because "unknown" is at least honest.
 */
export function probeBaseUrl(environment: ConsoleEnvironment): string {
  return import.meta.env.DEV ? '' : environment.managerBaseUrl;
}

export async function fetchWhoami(baseUrl: string): Promise<WhoAmI> {
  return getJsonAt<WhoAmI>(baseUrl, '/v1/whoami');
}

/** The machine code `shared/PrincipalHelper.ps1` refuses a foreign tenant with. */
export const FORBIDDEN_TENANT = 'FORBIDDEN_TENANT';

function envelopeErrorCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const error = (body as { error?: unknown }).error;
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/**
 * "This tenant may not reach that environment" — and NOTHING else.
 *
 * BOTH conditions are required, and that is why this is a named predicate rather
 * than an inline `status === 403`.
 *
 *   - **Not 403 alone.** The Manager has other 403s (environment reset disabled,
 *     for one), and an environment that refused one operation has not refused
 *     the operator.
 *   - **Not the code alone.** A code is a string in a body; pairing it with the
 *     status the guard actually returns means a mislabelled body cannot hide an
 *     environment by itself.
 *
 * Everything else — a 500, a timeout, a CORS failure, a Manager that is simply
 * down — is NOT this. An environment that is broken must not be presented as one
 * the operator lacks access to: those two states have different operator
 * responses (raise an incident vs ask for consent), and collapsing them sends
 * someone to the wrong place at the worst possible time.
 */
export function isForbiddenTenantError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status !== 403) return false;
  return envelopeErrorCode(error.body) === FORBIDDEN_TENANT;
}
